import React, { useCallback, useMemo } from 'react';
import { FileText } from 'react-feather';
import {
  ClioDocument,
  ClioFolder,
  DocumentPickerFilterMode,
  DOCUMENT_PICKER_FILTER_MODES,
} from '~/src/types/clioDocumentPicker';
import { getFormattedDateFromIsoString } from '~/src/utils/date';

interface DocumentPickerContentProps {
  documents: ClioDocument[];
  folders: ClioFolder[];
  selectedDocuments: ClioDocument[];
  isLoading: boolean;
  _hasMore: boolean;
  _error: string | null;
  onDocumentToggle: (document: ClioDocument) => void;
  onFolderNavigate: (folderId: number) => void;
  onSelectAll?: (documents: ClioDocument[]) => void;
  _onLoadMore: () => void;
  _onRetry: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onClearSearch: () => void;

  _breadcrumbs?: Array<{ id: number; name: string; type: string }>;
  filterMode: DocumentPickerFilterMode;
  onFilterChange: (mode: DocumentPickerFilterMode) => void;
  isAtSelectionLimit?: boolean;

  // Pagination props
  totalCount: number;
  currentPage: number;
  itemsPerPage: number;
  _totalPages: number;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  canGoPrevious: boolean;
  canGoNext: boolean;
}

export const DocumentPickerContent: React.FC<DocumentPickerContentProps> = ({
  documents,
  folders,
  selectedDocuments,
  isLoading,
  _hasMore,
  _error,
  onDocumentToggle,
  onFolderNavigate,
  onSelectAll,
  _onLoadMore,
  _onRetry,
  searchQuery,
  onSearchChange,
  onClearSearch,

  _breadcrumbs = [],
  filterMode,
  onFilterChange,
  isAtSelectionLimit = false,

  // Pagination props
  totalCount,
  currentPage,
  itemsPerPage,
  _totalPages,
  onPreviousPage,
  onNextPage,
  onItemsPerPageChange,
  canGoPrevious,
  canGoNext,
}) => {
  const formatFileSize = (bytes: number | undefined): string => {
    if (!bytes || bytes === 0) return '-';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  // Combine folders and documents for the table
  const combinedData = useMemo(
    () => [
      ...folders.map((folder) => ({
        id: `folder-${folder.id}`,
        type: 'folder' as const,
        name: folder.name,
        matter: folder.matter?.display_number || '-',
        fileSize: '-',
        lastEdited: getFormattedDateFromIsoString(folder.updated_at),
        isSelected: false,
        originalItem: folder,
      })),
      ...documents.map((document) => ({
        id: `document-${document.id}`,
        type: 'document' as const,
        name: document.filename,
        matter: document.matter?.display_number || '-',
        fileSize: formatFileSize(document.size),
        lastEdited: getFormattedDateFromIsoString(document.updated_at),
        isSelected: selectedDocuments.some(
          (selected) => selected.id === document.id,
        ),
        originalItem: document,
      })),
    ],
    [folders, documents, selectedDocuments],
  );

  const handleRowClick = useCallback(
    (rowData: any) => {
      if (rowData.type === 'folder') {
        onFolderNavigate(rowData.originalItem.id);
      } else if (rowData.type === 'document') {
        // Don't allow selection if at limit and document is not already selected
        if (isAtSelectionLimit && !rowData.isSelected) {
          return;
        }
        onDocumentToggle(rowData.originalItem);
      }
    },
    [onFolderNavigate, onDocumentToggle, isAtSelectionLimit],
  );

  // Get selectable documents from current data
  const selectableDocuments = combinedData
    .filter((item) => item.type === 'document')
    .map((item) => item.originalItem as ClioDocument);

  // Calculate header checkbox state
  const selectedSelectableDocuments = selectableDocuments.filter((doc) =>
    selectedDocuments.some((selected) => selected.id === doc.id),
  );

  const allSelectableSelected =
    selectableDocuments.length > 0 &&
    selectedSelectableDocuments.length === selectableDocuments.length;
  const someSelectableSelected = selectedSelectableDocuments.length > 0;
  const isIndeterminate = someSelectableSelected && !allSelectableSelected;

  // Handle select all checkbox change
  const handleSelectAllChange = useCallback(() => {
    if (!onSelectAll) return;

    if (allSelectableSelected) {
      // Deselect all currently selected selectable documents
      onSelectAll(selectedSelectableDocuments);
    } else {
      // Select all selectable documents that are not currently selected
      const documentsToSelect = selectableDocuments.filter(
        (doc) => !selectedDocuments.some((selected) => selected.id === doc.id),
      );
      onSelectAll(documentsToSelect);
    }
  }, [
    onSelectAll,
    allSelectableSelected,
    selectedSelectableDocuments,
    selectableDocuments,
    selectedDocuments,
  ]);

  // Memoized event handlers to prevent recreation on each render
  const handleFilterAll = useCallback(() => {
    onFilterChange(DOCUMENT_PICKER_FILTER_MODES.ALL);
  }, [onFilterChange]);

  const handleFilterFilesOnly = useCallback(() => {
    onFilterChange(DOCUMENT_PICKER_FILTER_MODES.FILES_ONLY);
  }, [onFilterChange]);

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(e.target.value);
    },
    [onSearchChange],
  );

  const handleSelectAllClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleSelectAllChange();
    },
    [handleSelectAllChange],
  );

  const handleCheckboxClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleRowClickEvent = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const itemIndex = parseInt(
        target.getAttribute('data-item-index') || '0',
        10,
      );
      const item = combinedData[itemIndex];
      if (item) {
        handleRowClick(item);
      }
    },
    [combinedData, handleRowClick],
  );

  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const target = e.currentTarget;
      const itemIndex = parseInt(
        target.getAttribute('data-item-index') || '0',
        10,
      );
      const item = combinedData[itemIndex];
      if (item && item.type === 'document') {
        onDocumentToggle(item.originalItem);
      }
    },
    [combinedData, onDocumentToggle],
  );

  const columns = [
    {
      label: '',
      labelRender: () => {
        const renderCheckboxContent = () => {
          if (allSelectableSelected || isIndeterminate) {
            // Show white minus icon when all are selected
            return (
              <svg
                className="h-3 w-3 text-white"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <rect x="2" y="7" width="12" height="2" rx="1" />
              </svg>
            );
          }
          // Empty for unchecked state
          return null;
        };

        return (
          <div className="flex flex-1 items-center justify-center">
            <button
              type="button"
              disabled={selectableDocuments.length === 0 || !onSelectAll}
              onClick={handleSelectAllClick}
              className={`h-4 w-4 rounded-sm border flex items-center justify-center transition-colors ${
                allSelectableSelected || isIndeterminate
                  ? 'bg-blue-600 border-blue-600'
                  : 'border-gray-300 hover:border-gray-400'
              } ${
                selectableDocuments.length === 0
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer'
              }`}
              title={
                selectableDocuments.length === 0
                  ? 'No selectable documents'
                  : allSelectableSelected
                  ? 'Deselect all'
                  : 'Select all'
              }
            >
              {renderCheckboxContent()}
            </button>
          </div>
        );
      },
      dataKey: 'checkbox',
      className: 'w-[60px] min-w-[60px] max-w-[60px] flex-none',
      hideLabel: false,
      disableSort: true,
      render: (value: any, { rowData }: any, itemIndex: number) => (
        <div className="w-[60px] flex justify-center">
          <input
            type="checkbox"
            data-item-index={itemIndex}
            checked={rowData.type === 'document' && rowData.isSelected}
            onChange={handleCheckboxChange}
            disabled={
              rowData.type === 'folder' ||
              (rowData.type === 'document' &&
                !rowData.isSelected &&
                isAtSelectionLimit)
            }
            className={`h-4 w-4 text-blue-600 rounded border-gray-300 ${
              rowData.type === 'document' &&
              !rowData.isSelected &&
              isAtSelectionLimit
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
            onClick={handleCheckboxClick}
          />
        </div>
      ),
    },
    {
      label: 'Name',
      dataKey: 'name',
      className: 'min-w-[300px] flex-1',
      render: (value: any, { rowData }: any) => (
        <div className="flex items-center min-w-[300px] flex-1">
          {rowData.type === 'folder' ? (
            <svg
              className="w-5 h-5  text-gray-500 mr-3 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
            </svg>
          ) : (
            <FileText className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0" />
          )}
          <span className="text-sm text-gray-900 truncate">{rowData.name}</span>
        </div>
      ),
    },
    {
      label: 'Matter',
      dataKey: 'matter',
      className: 'flex-1 min-w-[150px]',
      render: (value: any, { rowData }: any) => (
        <div className="flex-1 min-w-[150px]">
          <span className="text-sm text-gray-900 truncate block">
            {rowData.matter}
          </span>
        </div>
      ),
    },
    {
      label: 'File size',
      dataKey: 'fileSize',
      className: 'w-[100px] min-w-[100px] flex-none',
      render: (value: any, { rowData }: any) => (
        <div className="w-[100px]">
          <span className="text-sm text-gray-690">{rowData.fileSize}</span>
        </div>
      ),
    },
    {
      label: 'Last edited at',
      dataKey: 'lastEdited',
      className: 'w-[120px] min-w-[120px] flex-none',
      render: (value: any, { rowData }: any) => (
        <div className="w-[120px]">
          <span className="text-sm text-gray-900">{rowData.lastEdited}</span>
        </div>
      ),
    },
  ];

  return (
    <div className="flex-1 flex flex-col bg-white min-h-0 h-full">
      {/* Search and Filters */}
      <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0 h-auto">
        <div className="flex items-center justify-between gap-4">
          {/* Filter buttons */}
          <div className="inline-block p-0.5 items-center gap-1 border border-solid rounded-md border-gray-200">
            <button
              onClick={handleFilterAll}
              className={`px-3 py-2 mr-1 rounded-md text-sm font-medium transition-colors ${
                filterMode === DOCUMENT_PICKER_FILTER_MODES.ALL
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-900 hover:bg-gray-100'
              }`}
            >
              All
            </button>
            <button
              onClick={handleFilterFilesOnly}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                filterMode === DOCUMENT_PICKER_FILTER_MODES.FILES_ONLY
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-900 hover:bg-gray-100'
              }`}
            >
              Files only
            </button>
          </div>
          {/* Search input */}
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={handleSearchInputChange}
              className="w-64 pl-10 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              aria-label="Search documents"
            />
            {searchQuery && (
              <button
                onClick={onClearSearch}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded transition-colors"
                aria-label="Clear search"
              >
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Custom Table with Scrollable Body */}
      <div className="flex-1 flex flex-col min-h-0 ">
        {/* Table Header */}
        <div className="flex-shrink-0 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center h-12 px-4">
            {columns.map((column, index) => (
              <div
                key={`${column.label}-${index}`}
                className={`flex items-center text-xs font-semibold text-gray-900 capitalize tracking-wide ${
                  column.className || ''
                }`}
              >
                {column.hideLabel
                  ? null
                  : column.labelRender
                  ? column.labelRender()
                  : column.label}
              </div>
            ))}
          </div>
        </div>

        {/* Scrollable Table Body */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-gray-900">Loading...</div>
            </div>
          ) : combinedData.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-gray-900">No documents found</div>
            </div>
          ) : (
            <div>
              {combinedData.map((item, itemIndex) => (
                <div
                  key={item.id}
                  data-item-index={itemIndex}
                  className={`flex items-center border-b border-gray-100 min-h-[48px] px-4 ${
                    item.type === 'document' &&
                    !item.isSelected &&
                    isAtSelectionLimit
                      ? 'opacity-50 cursor-not-allowed bg-gray-50'
                      : 'hover:bg-gray-50 cursor-pointer'
                  }`}
                  onClick={handleRowClickEvent}
                >
                  {columns.map((column, columnIndex) => (
                    <div
                      key={columnIndex}
                      className={`flex items-center ${column.className || ''}`}
                    >
                      {column.render
                        ? column.render(
                            (item as any)[column.dataKey],
                            {
                              rowData: item,
                            },
                            itemIndex,
                          )
                        : (item as any)[column.dataKey]}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <button
                onClick={onPreviousPage}
                disabled={!canGoPrevious || isLoading}
                className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={onNextPage}
                disabled={!canGoNext || isLoading}
                className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                {totalCount > 0 ? (
                  <>
                    {Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)}
                    -{Math.min(currentPage * itemsPerPage, totalCount)} of{' '}
                    {totalCount} items
                  </>
                ) : (
                  '0 items'
                )}
              </div>

              <div className="flex items-center gap-2">
                <select
                  value={itemsPerPage}
                  onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isLoading}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
